package com.arena_web.ui.components.widgets

import androidx.compose.runtime.Composable
import com.arena_web.ui.theme.AppColors
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Pro Play brand loader — Figma 10035:243951 / 10124:146726.
 *
 * Renders the 6-segment Pro Play logo as inline SVG and animates each
 * segment outward from the logo center and back, so the mark visibly
 * "expands and reassembles" on a 1.6s ease-in-out loop. Per-segment
 * translation vectors point from the logo centre toward that segment's
 * own centroid, so every petal flies straight outward in its natural
 * direction with no rotation.
 */
@Composable
fun LoadingSpinner(
    modifier: Modifier = Modifier,
    size: CSSLengthOrPercentageNumericValue = 40.px,
    text: String? = null,
) {
    Box(
        modifier = modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.gap(16.px),
        ) {
            BrandLogoLoader(size = size)

            text?.let {
                SpanText(
                    text = it,
                    modifier = Modifier
                        .color(AppColors.Primary)
                        .fontSize(14.px),
                )
            }
        }
    }
}

@Composable
fun InlineSpinner(
    modifier: Modifier = Modifier,
    size: CSSLengthOrPercentageNumericValue = 20.px,
) {
    Box(modifier = modifier) {
        BrandLogoLoader(size = size)
    }
}

@Composable
private fun BrandLogoLoader(size: CSSLengthOrPercentageNumericValue) {
    Div(
        attrs = {
            ref { element ->
                element.innerHTML = BRAND_LOADER_SVG
                onDispose { }
            }
            style {
                property("width", size.toString())
                property("height", "auto")
                // Preserve the logo's natural 185:128 aspect ratio.
                property("aspect-ratio", "185 / 128")
                property("display", "inline-block")
                property("line-height", "0")
            }
        },
    )
}

// Segments are tagged seg-1 … seg-6 in clockwise order from the top arc.
// Source SVG order (Vector_1..Vector_6) → clockwise slot:
//   Vector_6 (top arc)        → seg-1   outward: ( 0,  -1)
//   Vector_5 (upper-right)    → seg-2   outward: ( 0.76, -0.65)
//   Vector_3 (lower-right)    → seg-3   outward: ( 0.99,  0.14)
//   Vector_1 (bottom arc)     → seg-4   outward: ( 0,   1)
//   Vector_2 (lower-left)     → seg-5   outward: (-0.99,  0.14)
//   Vector_4 (upper-left)     → seg-6   outward: (-0.76, -0.65)
// Translation magnitude is 14 viewBox units → scales naturally with the
// rendered loader size. Whole-group rotate+scale layers a subtle breath.
private val BRAND_LOADER_SVG = """
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 185 128" fill="none"
     preserveAspectRatio="xMidYMid meet"
     style="display:block;width:100%;height:100%;overflow:visible;">
  <defs>
    <linearGradient id="ppLoaderFill" x1="92.5" y1="0" x2="92.5" y2="128" gradientUnits="userSpaceOnUse">
      <stop stop-color="#1778FE"/>
      <stop offset="1" stop-color="#0051EF"/>
    </linearGradient>
    <linearGradient id="ppLoaderStroke" x1="92.5" y1="0" x2="92.5" y2="128" gradientUnits="userSpaceOnUse">
      <stop stop-color="#2F8FFF"/>
      <stop offset="1" stop-color="#1668FF"/>
    </linearGradient>
  </defs>
  <style>
    .pp-loader-seg {
      fill: url(#ppLoaderFill);
      stroke: url(#ppLoaderStroke);
      stroke-width: 2.42;
      transform-box: fill-box;
      transform-origin: center;
      animation: pp-loader-expand 1.6s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    }
    .pp-loader-seg.seg-1 { --dx:   0px; --dy: -14px; }
    .pp-loader-seg.seg-2 { --dx:  11px; --dy:  -9px; }
    .pp-loader-seg.seg-3 { --dx:  14px; --dy:   2px; }
    .pp-loader-seg.seg-4 { --dx:   0px; --dy:  14px; }
    .pp-loader-seg.seg-5 { --dx: -14px; --dy:   2px; }
    .pp-loader-seg.seg-6 { --dx: -11px; --dy:  -9px; }
    @keyframes pp-loader-expand {
      0%, 100% { transform: translate(0, 0); opacity: 0.85; }
      50%      { transform: translate(var(--dx), var(--dy)); opacity: 1; }
    }
  </style>
  <g class="pp-loader-root">
    <path class="pp-loader-seg seg-4" d="M120.673 85.0174C122.167 85.0146 123.808 87.4422 124.748 88.5441C130.112 94.827 135.264 101.299 140.576 107.626C141.163 108.645 141.563 109.544 141.097 110.699C140.634 111.845 139.565 112.235 138.534 112.799C113.308 126.579 80.1722 127.387 53.6794 116.641C50.9736 115.545 45.8371 113.487 43.589 111.884C42.2383 110.921 42.1289 109.25 42.9997 107.929C48.9528 101.21 54.3559 93.4544 60.4534 86.9152C61.5366 85.7538 62.2958 84.8107 64.082 85.1103C65.2586 85.3088 67.4536 86.3413 68.7789 86.7428C83.6192 91.2497 99.4715 91.4365 114.379 87.0461C116.172 86.5181 118.189 85.5787 119.914 85.1491C120.161 85.0868 120.415 85.0174 120.671 85.0174H120.673Z"/>
    <path class="pp-loader-seg seg-5" d="M11.7259 39.3424C12.8653 39.2744 16.7231 41.3092 17.9226 41.9353C22.4093 44.2792 26.691 47.0533 31.0623 49.6009C31.1875 49.6738 31.2969 49.7722 31.3797 49.8913C32.2667 51.1701 31.1283 53.6025 31.013 55.1392C30.0796 67.7141 42.6702 75.6012 52.6154 80.2131C53.7075 80.7191 56.5471 81.61 57.1937 82.4339C58.0591 83.5345 57.6235 84.1577 56.9325 85.1194C52.0467 91.9207 45.6272 98.1569 40.6457 104.963C40.6004 105.025 40.5481 105.082 40.489 105.132C39.044 106.354 37.813 106.268 36.1384 105.507C31.9918 103.62 26.7915 99.9968 23.2481 97.1036C6.79373 83.6664 -2.25646 61.7672 8.99665 41.8713C9.64323 40.7284 10.2119 39.4329 11.7259 39.3424Z"/>
    <path class="pp-loader-seg seg-3" d="M173.275 39.3424C172.136 39.2744 168.278 41.3092 167.078 41.9353C162.675 44.2359 158.469 46.9509 154.181 49.4592C153.722 49.7279 153.395 50.1837 153.318 50.7094C153.123 52.0423 153.895 53.887 153.988 55.1382C154.921 67.7131 142.331 75.6002 132.386 80.2121C131.294 80.7181 128.454 81.609 127.807 82.433C126.942 83.5335 127.378 84.1567 128.069 85.1184C132.936 91.8941 139.325 98.1077 144.297 104.883C144.388 105.006 144.493 105.119 144.612 105.215C146.013 106.348 147.225 106.249 148.861 105.506C153.007 103.619 158.208 99.9958 161.751 97.1026C178.206 83.6654 187.256 61.7663 176.003 41.8703C175.357 40.7274 174.788 39.432 173.274 39.3414L173.275 39.3424Z"/>
    <path class="pp-loader-seg seg-6" d="M65.7618 4.02405C66.85 3.89411 67.6838 4.28787 68.1678 5.27327C70.1282 9.26801 71.6639 14.0247 73.1788 18.3374C73.5681 20.7118 71.3632 20.699 69.7675 21.0947C57.3572 24.168 46.3052 29.8727 37.7468 39.4865C36.6261 40.7455 35.5902 42.354 34.4784 43.5275C33.4169 44.6487 32.4588 44.7412 31.0503 44.125C26.2808 42.0351 21.6 38.2786 16.8482 36.0075C16.0301 35.3952 15.9089 34.5723 16.4007 33.6814C16.9734 32.6438 18.6302 30.7882 19.4611 29.8176C29.429 18.175 43.8243 10.0811 58.4759 5.85112C60.5783 5.24374 63.6545 4.27705 65.7598 4.02602L65.7618 4.02405Z"/>
    <path class="pp-loader-seg seg-2" d="M119.239 4.02405C118.151 3.89411 117.317 4.28787 116.833 5.27327C115.136 9.44422 113.32 14.0562 111.822 18.3374C111.433 20.7118 113.638 20.699 115.233 21.0947C127.644 24.168 138.696 29.8727 147.254 39.4865C148.375 40.7455 149.411 42.354 150.522 43.5275C151.584 44.6487 152.542 44.7412 153.951 44.125C158.72 42.0351 163.401 38.2786 168.153 36.0075C168.971 35.3952 169.092 34.5723 168.6 33.6814C168.028 32.6438 166.371 30.7882 165.54 29.8176C155.572 18.175 141.177 10.0811 126.525 5.85112C124.423 5.24374 121.346 4.27705 119.241 4.02602L119.239 4.02405Z"/>
    <path class="pp-loader-seg seg-1" d="M89.9281 27.0562C108.51 26.561 132.419 34.3516 139.447 52.9462C140.27 55.1237 141.486 57.9804 138.785 59.3439C136.935 60.2771 133.635 61.4101 131.598 62.1002C130.335 62.5284 129.543 62.9143 128.339 62.0106C127.417 61.3196 127.35 60.2584 126.887 59.2799C118.005 40.5308 88.2782 37.4535 71.1013 45.631C66.0046 48.0576 60.9679 52.0001 57.9992 56.7814C57.0973 58.2334 56.5434 60.0241 55.7223 61.358C55.0314 62.4802 53.9008 62.8277 52.7151 62.3896C50.048 61.4062 47.2941 60.4277 44.7728 59.337C41.7922 57.8042 43.8611 53.5654 44.9078 51.3111C52.4667 35.0348 72.7572 27.5139 89.9291 27.0562H89.9281Z"/>
  </g>
</svg>
""".trimIndent()
